import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { Route } from './AppSidebar';
import { useAppTFunction } from '@retail/app/i18n';
import { useMatch } from 'react-router';
import { ResourceRoute } from './useNavRoutes';
import { useCallback, useMemo } from 'react';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { EyeIcon } from '@shared/custom-icons';
import { Badge } from '@mui/material';
import { useIdle } from '@uidotdev/usehooks';
import { useFetchDraftConditions } from '@retail/calculus/data-access';
import { PriceCheck, Settings } from '@mui/icons-material';
import { useFetchPriceControlAggregated } from '@retail/price-control/data-access';

export const useActionsNavRoutes = (): Route[] => {
  const { userPermissions, userCanRead } = usePermissionsForCurrentUser();
  const t = useAppTFunction();
  const { assortmentId } = useSelectedOrgUnit();
  const idle = useIdle(30000);
  const hasApprovalPermission = userCanRead('APPROVAL');
  const priceControlIsEnabled = useIsFeatureEnabled('price-control');

  const { data: conditionsDTO } = useFetchDraftConditions({
    assortmentId,
    refetchInterval: idle ? undefined : 30000,
    suspense: false,
    enabled: hasApprovalPermission,
  });

  const { data: priceControlDTO } = useFetchPriceControlAggregated(assortmentId, {
    suspense: false,
    enabled: priceControlIsEnabled,
  });

  const approvalRouteMatch = useMatch(`${mgPrisRoutes.approval.fullRoutePath}/*`);
  const priceControlRouteMatch = useMatch(`${mgPrisRoutes.priceControl.fullRoutePath}/*`);
  const memberConfigRouteMatch = useMatch(`${mgPrisRoutes.memberConfig.fullRoutePath}/*`);

  const CustomEyeIcon = useCallback(
    () => (
      <Badge
        badgeContent={conditionsDTO?.length ?? 0}
        sx={{
          '.MuiBadge-badge': {
            color: 'common.white',
            background: (theme) => theme.vars.palette.warning.main,
            top: '3px',
            right: '1px',
            width: 10,
            border: (theme) => {
              const palette = theme.vars.palette;
              return `2.5px solid ${
                approvalRouteMatch ? palette.nav?.activeLight : palette.background.paper
              }`;
            },
          },
        }}
      >
        <EyeIcon />
      </Badge>
    ),
    [approvalRouteMatch, conditionsDTO?.length]
  );

  const CustomPriceControlIcon = useCallback(
    () => (
      <Badge
        badgeContent={priceControlDTO?.suppliers?.length ?? 0}
        sx={{
          '.MuiBadge-badge': {
            color: 'common.white',
            background: (theme) => theme.vars.palette.warning.main,
            top: '3px',
            right: '1px',
            width: 10,
          },
        }}
      >
        <PriceCheck />
      </Badge>
    ),
    [priceControlDTO?.suppliers?.length]
  );

  const actionsRoutes: ResourceRoute[] = useMemo(
    () => [
      {
        title: t('app.routes.approval'),
        to: mgPrisRoutes.approval.fullRoutePath,
        Icon: CustomEyeIcon,
        isActive: !!approvalRouteMatch,
        resource: 'APPROVAL',
      },
      {
        title: t('app.routes.memberConfig'),
        to: mgPrisRoutes.memberConfig.getFullLinkPath(),
        Icon: Settings,
        isActive: !!memberConfigRouteMatch,
        resource: 'MEMBER_CONFIG',
      },
    ],
    [CustomEyeIcon, approvalRouteMatch, memberConfigRouteMatch, t]
  );

  const enabledActionRoutes = useMemo(
    () =>
      priceControlIsEnabled
        ? [
            {
              title: t('app.routes.priceControl'),
              to: mgPrisRoutes.priceControl.fullRoutePath,
              Icon: CustomPriceControlIcon,
              isActive: !!priceControlRouteMatch,
              resource: 'PRICE_CONTROL',
            },
            ...actionsRoutes,
          ]
        : actionsRoutes,
    [priceControlIsEnabled, t, CustomPriceControlIcon, priceControlRouteMatch, actionsRoutes]
  );

  return useMemo(
    () =>
      enabledActionRoutes.filter(({ resource }) =>
        userPermissions.some(
          (permission) => permission.resource === resource && permission.canRead()
        )
      ),
    [enabledActionRoutes, userPermissions]
  );
};
